import { Workbox } from 'workbox-window';

import { usePwaUpdateStore } from '../stores/pwaUpdateStore';

let wb: Workbox | null = null;
let reloading = false;

export function registerSW() {
  if ('serviceWorker' in navigator && import.meta.env.PROD) {
    wb = new Workbox('/sw.js');

    // A new build is installed and waiting. Don't take over yet — flag it and
    // let UpdatePrompt decide when to activate (silently on safe screens, or
    // behind a prompt during an inspection so we never reload over live work).
    wb.addEventListener('waiting', () => {
      usePwaUpdateStore.getState().setUpdateReady(true);
    });

    // The waiting SW has taken control -> load the fresh build. Guard against
    // a double reload.
    wb.addEventListener('controlling', () => {
      if (reloading) return;
      reloading = true;
      window.location.reload();
    });

    wb.register();

    // A timer alone isn't enough: installed mobile PWAs suspend background
    // timers, so an app left open would never re-check. Re-check on a timer,
    // on foreground (visibilitychange), and on focus — whichever comes first.
    setInterval(() => void checkForUpdate(), 30 * 60 * 1000);
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') void checkForUpdate();
    });
    window.addEventListener('focus', () => void checkForUpdate());
  }
}

/**
 * Activate the waiting build. Sends SKIP_WAITING to the new SW, which then
 * fires 'controlling' and triggers the reload above.
 */
export function applyUpdate() {
  wb?.messageSkipWaiting();
}

/**
 * Ask the browser to check for a new SW now. Resolves once the check is done;
 * if a new build exists, the 'waiting' handler flips updateReady. Returns false
 * when SW isn't available (dev / unsupported).
 */
export async function checkForUpdate(): Promise<boolean> {
  if (!wb) return false;
  try {
    await wb.update();
    return true;
  } catch {
    return false;
  }
}
